import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import DischargeForm from 'components/dischargeForm/DischargeForm';
import { DischargeFormMeta, DischargeFormValues } from 'components/dischargeForm/DischargeInformation';
import { buildAnsweredQuestionsFromFormValues } from 'components/dischargeForm/helpers';
import dischargeFormValidation from 'components/dischargeForm/validation';
import { defaultActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import StandardModal from 'components/shared/StandardModal';
import { Flags } from 'constants/flags';
import { FormProvider } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import { useProfile } from 'context/profile';
import { useTranslation } from 'context/translations';
import { UploadProvider } from 'context/upload';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import { simpleDashDate } from 'lib/date';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import LocationEpisodeDischarge from 'models/LocationEpisodeDischarge';
import {
  createLocationEpisodeDischarge,
  showLocationEpisodeDischarge,
  updateLocationEpisodeDischarge,
} from 'services/api/locationEpisodeDischarges';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { useToastActions } from 'stores/toastStore';

export type DischargeModalType = {
  setShow: (b: boolean) => void;
  patientName: string;
  episode: Episode;
  locationEpisode: LocationEpisode;
  invalidateData?: () => void;
};

export default function DischargeModal(props: DischargeModalType) {
  const { setShow, patientName, episode, invalidateData } = props;

  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const [locationEpisode, setLocationEpisode] = useState<LocationEpisode>(props.locationEpisode);
  const [subtitle, setSubtitle] = useState<string | undefined>();
  const { translate } = useTranslation();

  const isDischargeEdit = locationEpisode.id === episode.latestLocationEpisodeDischarge?.locationEpisodeId;

  const locationEpisodeDischargeQueryParams = {
    id: episode.latestLocationEpisodeDischarge?.id ?? '',
    locationEpisodeId: locationEpisode.id,
    include:
      'group_type,group,location_episode.owner.client.enabled_flags,location_episode.question_templates,discharge_reason.discharged_to_group_type,activity,created_by,pcc_discharge_report',
  };

  const { data: locationEpisodeDischarge } = useQuery({
    queryKey: locationEpisodeQueryKeys.show(locationEpisodeDischargeQueryParams),
    queryFn: () => showLocationEpisodeDischarge(locationEpisodeDischargeQueryParams),
    enabled: isDischargeEdit,
  });

  useEffect(() => {
    if (isDischargeEdit && locationEpisodeDischarge?.locationEpisode) {
      setLocationEpisode(locationEpisodeDischarge.locationEpisode);
    }
  }, [isDischargeEdit, locationEpisode, locationEpisodeDischarge]);

  const { profile } = useProfile();
  const linkedLocationEpisodes =
    !isDischargeEdit && profile?.canAccessGroup(locationEpisode.group.id) ? locationEpisode.sharedRecords : [];

  const { mutate } = useMutation({
    mutationFn: isDischargeEdit ? updateLocationEpisodeDischarge : createLocationEpisodeDischarge,
  });

  const cancel = () => {
    setShow(false);
  };

  const handleSubmit = async (
    values: FormValues<DischargeFormValues>,
    _meta: FormMeta<DischargeFormMeta>,
    helpers: FormHelpers<DischargeFormValues, DischargeFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    const dischargeValues = LocationEpisodeDischarge.fromFormValues(values).serialize();

    const primaryQuestions = buildAnsweredQuestionsFromFormValues(
      locationEpisode.dischargeTemplate?.questions || [],
      values.questions || {}
    );

    const linkedDischarges = linkedLocationEpisodes.map((linkedLE) => ({
      locationEpisodeId: linkedLE.id,
      dischargeQuestions: buildAnsweredQuestionsFromFormValues(
        linkedLE.dischargeTemplate?.questions || [],
        values.linkedQuestions?.[linkedLE.id] || {}
      ),
    }));

    mutate(
      {
        ...dischargeValues,
        locationEpisodeId: locationEpisode.id,
        dischargeQuestions: primaryQuestions,
        enteredAt: simpleDashDate(values.actualDischargeDate ?? new Date()) as string,
        note: values.note
          ? {
              ...values.note,
              attachments: values.note.attachments?.map((upload) => upload.serialize()),
            }
          : undefined,
        ...(!isDischargeEdit && linkedDischarges.length > 0 && { linkedDischarges }),
      },
      {
        onSuccess: () => {
          invalidateQueries({
            episodes: true,
            activities: true,
          });
          invalidateData?.();
          cancel();
        },
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again' });
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  const initialValues = {
    ...(isDischargeEdit && locationEpisodeDischarge?.dischargeFormValues()),
    note: defaultActivityInputValues,
  };

  return (
    <StandardModal
      title={isDischargeEdit ? `Edit ${translate('Discharge')}` : `${translate('Discharge')} Patient`}
      subtitle={subtitle}
      onCancel={cancel}>
      <FormProvider<DischargeFormValues, DischargeFormMeta>
        resetOnInitialValuesChange
        initialValues={initialValues}
        onSubmit={handleSubmit}
        yupSchema={dischargeFormValidation(locationEpisode.currentRehabState?.enteredAt)}>
        <UploadProvider>
          <DischargeForm
            locationEpisode={locationEpisode}
            linkedLocationEpisodes={linkedLocationEpisodes}
            patientName={patientName}
            onCancel={cancel}
            dischargeQuestionsFlagEnabled={locationEpisode.owner.client.hasFlag(Flags.DischargeQuestions)}
            showActualDischargeDate
            onSubtitleChange={setSubtitle}
            locationEpisodeDischarge={isDischargeEdit ? locationEpisodeDischarge : undefined}
          />
        </UploadProvider>
      </FormProvider>
    </StandardModal>
  );
}
